import { Link } from 'react-router-dom';
import { addDays, barGeometry, daysBetween, shortDayLabel } from '../../lib/calendarDates';
import { useI18n } from '../../contexts/I18nContext';

export interface GanttBar {
  id: string;
  start: Date;
  /** null = open-ended: the bar runs to the horizon edge with a faded margin. */
  end: Date | null;
  /** Tailwind background class (e.g. "bg-blue-500"). */
  color?: string;
  label?: string;
  title?: string;
  onClick?: () => void;
}

export interface GanttRow {
  id: string;
  label: string;
  /** Router path — the label becomes a link when set. */
  labelHref?: string;
  bars: GanttBar[];
}

interface GanttChartProps {
  /** Monday local midnight; the horizon spans `weeks` full weeks from here. */
  horizonStart: Date;
  weeks: number;
  rows: GanttRow[];
}

const WEEK_PX = 64;

/**
 * Dependency-free presentational Gantt: a sticky left label column and a
 * horizontally scrollable timeline with absolutely positioned bars (left/width
 * percentages from barGeometry) plus a red "today" line.
 */
export function GanttChart({ horizonStart, weeks, rows }: GanttChartProps) {
  const { locale } = useI18n();
  const horizonEnd = addDays(horizonStart, weeks * 7);
  const totalDays = weeks * 7;
  const weekStarts = Array.from({ length: weeks }, (_, i) => addDays(horizonStart, i * 7));

  const todayOffset = daysBetween(horizonStart, new Date());
  const todayPct =
    todayOffset >= 0 && todayOffset < totalDays ? (todayOffset / totalDays) * 100 : null;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex">
        {/* Sticky label column. On a phone 192px would eat half the screen and
            leave the timeline unreadable, so it shrinks to 112px there. */}
        <div className="w-28 shrink-0 border-r border-gray-200 sm:w-48">
          <div className="h-8 border-b border-gray-200 bg-gray-50" />
          {rows.map((row) => (
            <div
              key={row.id}
              className="flex h-8 items-center border-b border-gray-100 px-2 last:border-b-0"
            >
              {row.labelHref ? (
                <Link
                  to={row.labelHref}
                  title={row.label}
                  className="truncate text-xs font-medium text-gray-900 hover:text-brand-600 hover:underline sm:text-sm"
                >
                  {row.label}
                </Link>
              ) : (
                <span
                  title={row.label}
                  className="truncate text-xs font-medium text-gray-900 sm:text-sm"
                >
                  {row.label}
                </span>
              )}
            </div>
          ))}
        </div>
        {/* Scrollable timeline */}
        <div className="flex-1 overflow-x-auto">
          <div className="relative" style={{ minWidth: weeks * WEEK_PX }}>
            <div className="flex h-8 border-b border-gray-200 bg-gray-50">
              {weekStarts.map((ws) => (
                <div
                  key={ws.getTime()}
                  className="shrink-0 border-r border-gray-100 px-1 py-1.5 text-xs text-gray-500"
                  style={{ width: `${100 / weeks}%` }}
                >
                  {shortDayLabel(locale, ws)}
                </div>
              ))}
            </div>
            {rows.map((row) => (
              <div key={row.id} className="relative h-8 border-b border-gray-100 last:border-b-0">
                {/* Week separators */}
                {weekStarts.slice(1).map((ws, i) => (
                  <div
                    key={ws.getTime()}
                    className="absolute inset-y-0 border-r border-gray-100"
                    style={{ left: `${((i + 1) / weeks) * 100}%` }}
                  />
                ))}
                {row.bars.map((bar) => {
                  const geo = barGeometry(horizonStart, horizonEnd, bar.start, bar.end);
                  if (!geo) return null;
                  const openEnded = bar.end === null || geo.clippedEnd;
                  return (
                    <div
                      key={bar.id}
                      role={bar.onClick ? 'button' : undefined}
                      onClick={bar.onClick}
                      title={bar.title}
                      className={`absolute top-1.5 flex h-5 items-center overflow-hidden rounded px-1.5 text-xs font-medium text-white ${
                        bar.color ?? 'bg-blue-500'
                      } ${bar.onClick ? 'cursor-pointer hover:opacity-90' : ''} ${
                        openEnded
                          ? 'rounded-r-none [mask-image:linear-gradient(to_right,black_85%,transparent)]'
                          : ''
                      }`}
                      style={{ left: `${geo.leftPct}%`, width: `${geo.widthPct}%` }}
                    >
                      <span className="truncate">{bar.label}</span>
                    </div>
                  );
                })}
              </div>
            ))}
            {/* Today marker */}
            {todayPct !== null && (
              <div
                className="pointer-events-none absolute inset-y-0 w-px bg-red-500"
                style={{ left: `${todayPct}%` }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
